import {useLocalStorage} from 'platform/components';
import {getStepsFromPixels} from 'platform/foundation';

import {useRef, useState} from 'react';

import {clamp, isNil} from 'ramda';

import {browserStorageKey} from '@dms/config';

import {useOnMount} from 'shared/web';

import {ACTIVITY_SIDEBAR_WIDTH} from '../constants/ACTIVITY_SIDEBAR_WIDTH';

const APP_CONTAINER_ID = 'dms-app-container';

export function useActivitySidebarWidth() {
  const [storedWidth, persistWidth] = useLocalStorage<number>(
    browserStorageKey.ACTIVITY_PANEL_WIDTH,
    ACTIVITY_SIDEBAR_WIDTH.DEFAULT
  );
  const [availableSteps, setAvailableSteps] = useState(
    ACTIVITY_SIDEBAR_WIDTH.MAX + ACTIVITY_SIDEBAR_WIDTH.MIN_CONTENT
  );
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | undefined>(undefined);

  const measure = () => {
    if (isNil(nodeRef.current)) {
      return;
    }

    const contentLeft =
      document.getElementById(APP_CONTAINER_ID)?.getBoundingClientRect().left ?? 0;
    const steps = getStepsFromPixels(nodeRef.current.getBoundingClientRect().right - contentLeft);
    if (isNil(steps)) {
      return;
    }

    setAvailableSteps(steps);
  };

  useOnMount(
    () => {
      const container = document.getElementById(APP_CONTAINER_ID);
      if (isNil(container)) {
        return;
      }

      observerRef.current = new ResizeObserver(measure);
      observerRef.current.observe(container);
    },
    () => observerRef.current?.disconnect()
  );

  const panelRef = (node: HTMLDivElement | null) => {
    nodeRef.current = node;
    if (isNil(node)) {
      return;
    }

    measure();
  };

  const bounds = {
    min: ACTIVITY_SIDEBAR_WIDTH.MIN,
    max: Math.max(
      ACTIVITY_SIDEBAR_WIDTH.MIN,
      Math.min(ACTIVITY_SIDEBAR_WIDTH.MAX, availableSteps - ACTIVITY_SIDEBAR_WIDTH.MIN_CONTENT)
    ),
  };

  const requestedWidth = Number.isFinite(storedWidth)
    ? storedWidth
    : ACTIVITY_SIDEBAR_WIDTH.DEFAULT;
  const width = clamp(bounds.min, bounds.max, requestedWidth);

  const setWidth = (nextWidth: number) => persistWidth(clamp(bounds.min, bounds.max, nextWidth));

  return {
    panelRef,
    width,
    bounds,
    setWidth,
    resetWidth: () => persistWidth(ACTIVITY_SIDEBAR_WIDTH.DEFAULT),
  };
}
